import {
  pgTable,
  text,
  uuid,
  char,
  boolean,
  timestamp,
  integer,
  smallint,
  jsonb,
  pgEnum,
  primaryKey,
  numeric,
  date,
  time,
  check,
  unique,
  uniqueIndex,
  index,
  foreignKey,
} from "drizzle-orm/pg-core";
import { relations, sql } from "drizzle-orm";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import {
  ORDER_STATUSES,
  SERVICE_TYPES,
  type ChecklistStage,
} from "./pre-analise/types";

// ---------------------------------------------------------------------------
// SECTION 2 — Shared enums (already exist in DB — Drizzle detects and skips CREATE)
// ---------------------------------------------------------------------------

export const empresaTipoEnum = pgEnum("empresa_tipo", [
  "OWNER",
  "VISTORIADORA",
  "PARCEIRO_DE_NEGOCIOS",
]);

export const tipoVeiculoEnum = pgEnum("tipo_veiculo", [
  "auto_leve",
  "moto",
  "caminhao",
  "onibus",
]);

// ---------------------------------------------------------------------------
// SECTION 3 — ReMonta enums (managed by explicit Supabase SQL migrations)
// ---------------------------------------------------------------------------

export const remontaLaudoStatusEnum = pgEnum("remonta_laudo_status", [
  "rascunho",
  "em_vistoria",
  "em_avaliacao_rt",
  "em_auditoria",
  "finalizado",
  "cancelado",
]);

export const remontaLaudoTipoEnum = pgEnum("remonta_laudo_tipo", [
  "reclassificacao",
  "avaliacao",
]);

export const remontaClassificacaoMontaEnum = pgEnum(
  "remonta_classificacao_monta",
  [
    "baixa_monta",
    "media_monta",
    "alta_monta",
    "remontagem_total",
    "pequena_monta",
    "grande_monta",
  ],
);

export const remontaNexoCausalClassificacaoEnum = pgEnum(
  "remonta_nexo_causal_classificacao",
  [
    "confirmado",
    "provavel",
    "parcial",
    "inconclusivo",
    "nao_demonstrado",
    "incompativel",
  ],
);

export const remontaOcorrenciaTipoEnum = pgEnum("remonta_ocorrencia_tipo", [
  "divergencia",
  "correcao",
  "auditoria",
  "observacao",
  "orcamento",
]);

export const remontaOrcamentoCategoriaEnum = pgEnum(
  "remonta_orcamento_categoria",
  [
    "peca",
    "mao_de_obra",
    "funilaria",
    "pintura",
    "mecanica",
    "eletrica",
    "diagnostico",
    "calibracao",
    "alinhamento",
    "transporte",
    "terceiros",
    "tributos",
    "nao_coberto",
  ],
);

export const remontaOrcamentoItemStatusEnum = pgEnum(
  "remonta_orcamento_item_status",
  ["proposto", "aprovado", "aprovado_com_ajuste", "devolvido", "reprovado"],
);

export const veiculoClassificacaoPorteEnum = pgEnum(
  "veiculo_classificacao_porte",
  ["pequena_monta", "media_monta", "grande_monta"],
);

export const remontaTipoFotoEnum = pgEnum("remonta_tipo_foto", [
  "veiculo_frente",
  "veiculo_traseira",
  "veiculo_lateral_dir",
  "veiculo_lateral_esq",
  "veiculo_interior",
  "veiculo_km",
  "veiculo_motor",
  "veiculo_identificacao",
  "elemento",
  "veiculo_45_dianteira_esq",
  "veiculo_45_dianteira_dir",
  "veiculo_45_traseira_esq",
  "veiculo_45_traseira_dir",
  "dano",
]);

export const remontaElementoStatusEnum = pgEnum("remonta_elemento_status", [
  "integro",
  "danificado",
  "substituido",
  "reparado",
  "inexistente",
  "nao_avaliado",
  "sim",
  "nao",
  "na",
]);

export const remontaOrdemServicoTipoEnum = pgEnum(
  "remonta_ordem_servico_tipo",
  SERVICE_TYPES,
);

export const remontaOrdemServicoStatusEnum = pgEnum(
  "remonta_ordem_servico_status",
  ORDER_STATUSES,
);

export const remontaChecklistEtapaEnum = pgEnum("remonta_checklist_etapa", [
  "documental",
  "pre_analise",
] as const satisfies readonly [ChecklistStage, ...ChecklistStage[]]);

// ---------------------------------------------------------------------------
// SECTION 4 — Shared tables (verbatim from original — empresas, userProfiles,
//             inviteCodes, userInvites)
// ---------------------------------------------------------------------------

export const empresas = pgTable("empresas", {
  id: uuid("id").primaryKey().defaultRandom(),
  tipo: empresaTipoEnum("tipo").notNull(),
  razaoSocial: text("razao_social").notNull(),
  nomeFantasia: text("nome_fantasia"),
  cnpj: text("cnpj"),
  cpf: text("cpf"),
  uf: char("uf", { length: 2 }).notNull(),
  email: text("email"),
  telefone: text("telefone"),
  whatsapp: text("whatsapp"),
  logradouro: text("logradouro"),
  numero: text("numero"),
  complemento: text("complemento"),
  bairro: text("bairro"),
  cidade: text("cidade").notNull(),
  cep: char("cep", { length: 8 }),
  vistoriadoraId: uuid("vistoriadora_id").references((): any => empresas.id, {
    onDelete: "set null",
  }),
  codigoConvite: text("codigo_convite").unique(),
  tagEmpresa: text("tag_empresa").unique(),
  logoUrl: text("logo_url"),
  idProprio: integer("id_proprio"),
  // ART da empresa parceira de negócios (campo compartilhado com LaudoLegal)
  art: text("art"),
  vencimentoArt: date("vencimento_art"),
  ativo: boolean("ativo").notNull().default(true),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

export const userProfiles = pgTable("user_profiles", {
  id: uuid("id").primaryKey(),
  empresaId: uuid("empresa_id")
    .references(() => empresas.id)
    .notNull(),
  nome: text("nome").notNull(),
  email: text("email").notNull(),
  roles: text("roles").array().notNull().default([]),
  sistema: text("sistema").array(), // nullable — legacy accounts (pre-phase-18) stay NULL, always allowed to login
  cpf: text("cpf"),
  telefone: text("telefone"),
  whatsapp: text("whatsapp"),
  logradouro: text("logradouro"),
  numero: text("numero"),
  complemento: text("complemento"),
  bairro: text("bairro"),
  cidade: text("cidade"),
  estado: char("estado", { length: 2 }),
  cep: char("cep", { length: 8 }),
  crea: text("crea"),
  assinaturaUrl: text("assinatura_url"),
  ativo: boolean("ativo").notNull().default(true),
  rebaixadoEm: timestamp("rebaixado_em", { withTimezone: true }),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

export const remontaCertificadosUsuario = pgTable("remonta_certificados_usuario", {
  userId: uuid("user_id").primaryKey().references(() => userProfiles.id),
  empresaId: uuid("empresa_id").notNull().references(() => empresas.id),
  cpfSha256: text("cpf_sha256").notNull(),
  encryptedCredentials: text("encrypted_credentials").notNull(),
  metadata: jsonb("metadata").notNull().$type<Omit<import('./user-certificate').UserCertificateView, 'updatedAt' | 'matchesCpf'>>(),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true }).notNull().defaultNow(),
}, table => [index("remonta_certificados_empresa_idx").on(table.empresaId)]);

export const inviteCodes = pgTable("invite_codes", {
  id: uuid("id").primaryKey().defaultRandom(),
  code: text("code").notNull().unique(),
  tipo: empresaTipoEnum("tipo").notNull(),
  vistoriadoraId: uuid("vistoriadora_id").references(() => empresas.id, {
    onDelete: "cascade",
  }),
  usos: integer("usos").notNull().default(0),
  maxUsos: integer("max_usos"),
  ativo: boolean("ativo").notNull().default(true),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

export const userInvites = pgTable("user_invites", {
  id: uuid("id").primaryKey().defaultRandom(),
  code: text("code").notNull().unique(),
  email: text("email"),
  empresaId: uuid("empresa_id")
    .notNull()
    .references(() => empresas.id, { onDelete: "cascade" }),
  roles: text("roles").array().notNull().default(["tv"]),
  usos: integer("usos").notNull().default(0),
  maxUsos: integer("max_usos").default(1),
  ativo: boolean("ativo").notNull().default(true),
  criadoPor: uuid("criado_por").references(() => userProfiles.id),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

export const notificacoes = pgTable("notificacoes", {
  id: uuid("id").primaryKey().defaultRandom(),
  usuarioId: uuid("usuario_id")
    .notNull()
    .references(() => userProfiles.id, { onDelete: "cascade" }),
  empresaId: uuid("empresa_id")
    .notNull()
    .references(() => empresas.id, { onDelete: "cascade" }),
  tipo: text("tipo").notNull(),
  titulo: text("titulo").notNull(),
  mensagem: text("mensagem").notNull(),
  laudoDbId: uuid("laudo_db_id"), // FK para laudos do LaudoLegal removida no banco próprio
  laudoId: text("laudo_id"),
  lida: boolean("lida").notNull().default(false),
  criadaEm: timestamp("criada_em", { withTimezone: true }).notNull().defaultNow(),
});

export type Notificacao = typeof notificacoes.$inferSelect;
export type InsertNotificacao = typeof notificacoes.$inferInsert;

export const remontaApks = pgTable("remonta_apks", {
  id: uuid("id").primaryKey().defaultRandom(),
  versao: text("versao").notNull(),
  nomeArquivo: text("nome_arquivo").notNull(),
  storagePath: text("storage_path").notNull(),
  tamanhoBytes: integer("tamanho_bytes").notNull(),
  principal: boolean("principal").notNull().default(false),
  enviadoPor: uuid("enviado_por").references(() => userProfiles.id),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

export type RemontaApk = typeof remontaApks.$inferSelect;
export type InsertRemontaApk = typeof remontaApks.$inferInsert;

// ---------------------------------------------------------------------------
// SECTION 5 — Shared veiculos table (shared between LaudoLegal and ReMonta)
// ---------------------------------------------------------------------------

export const veiculos = pgTable("veiculos", {
  id: uuid("id").primaryKey().defaultRandom(),
  placa: text("placa").unique(),
  chassi: text("chassi").unique(),
  tipoVeiculo: tipoVeiculoEnum("tipo").notNull(),
  marca: text("marca"),
  modelo: text("modelo"),
  anoFabricacao: integer("ano_fabricacao"),
  anoModelo: integer("ano_modelo"),
  cor: text("cor"),
  proprietario: text("proprietario"),
  renavam: text("renavam"),
  numeroMotor: text("numero_motor"),
  classificacaoPorte: veiculoClassificacaoPorteEnum("classificacao_porte"),
  valorFipe: numeric("valor_fipe", { precision: 12, scale: 2 }),
  parceiroId: uuid("parceiro_id").references(() => empresas.id),
  ativo: boolean("ativo").notNull().default(true),
  sistema: text("sistema").notNull().default("remonta"),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

// ---------------------------------------------------------------------------
// SECTION 6 — ReMonta domain tables (all prefixed remonta_)
// ---------------------------------------------------------------------------


export const remontaArts = pgTable("remonta_arts", {
  id: uuid("id").primaryKey().defaultRandom(),
  rtId: uuid("rt_id").notNull(), // plain uuid — logically references user_profiles.id (D-10)
  numeroArt: text("numero_art").notNull(),
  descricao: text("descricao"),
  valor: numeric("valor", { precision: 10, scale: 2 }),
  dataInicio: date("data_inicio").notNull(),
  dataFim: date("data_fim"),
  status: text("status").notNull().default("ativa"), // "ativa" | "encerrada"
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

export const remontaSolicitantePermissoes = pgTable(
  "remonta_solicitante_permissoes",
  {
    solicitanteId: uuid("solicitante_id")
      .notNull()
      .references(() => empresas.id, { onDelete: "cascade" }),
    vistoriadoraId: uuid("vistoriadora_id")
      .notNull()
      .references(() => empresas.id, { onDelete: "cascade" }),
    podeCriarDossie: boolean("pode_criar_dossie").notNull().default(false),
    concedidoPor: uuid("concedido_por")
      .notNull()
      .references(() => userProfiles.id),
    atualizadoEm: timestamp("atualizado_em", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (table) => [
    primaryKey({
      columns: [table.solicitanteId, table.vistoriadoraId],
    }),
  ],
);

export const remontaDossiesSinistro = pgTable(
  "remonta_dossies_sinistro",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    veiculoId: uuid("veiculo_id").references(() => veiculos.id, {
      onDelete: "restrict",
    }),
    solicitanteId: uuid("solicitante_id")
      .notNull()
      .references(() => empresas.id, { onDelete: "restrict" }),
    vistoriadoraId: uuid("vistoriadora_id")
      .notNull()
      .references(() => empresas.id, { onDelete: "restrict" }),
    criadoPor: uuid("criado_por")
      .notNull()
      .references(() => userProfiles.id),
    status: text("status").notNull().default("rascunho"),
    intakePreferencias: jsonb("intake_preferencias").$type<{ servicoInteresse?: string; primeiraOrdem?: { numeroOrdemExterna?: string; reguladorExterno?: string; vistoriaPrevistaEm?: string; prazoEntregaEm?: string } }>(),
    dataSinistro: date("data_sinistro").notNull(),
    numeroBat: text("numero_bat"),
    classificacaoOriginal: veiculoClassificacaoPorteEnum(
      "classificacao_original",
    ),
    valorFipe: numeric("valor_fipe", { precision: 12, scale: 2 }),
    mesReferenciaFipe: date("mes_referencia_fipe"),
    ufRegistro: char("uf_registro", { length: 2 }),
    motivoPendencia: text("motivo_pendencia"),
    inspecaoAgendadaEm: timestamp("inspecao_agendada_em", {
      withTimezone: true,
    }),
    localInspecao: text("local_inspecao"),
    objetoTipo: text("objeto_tipo").$type<"veiculo" | "imovel" | "outro">(),
    clienteEntidade: text("cliente_entidade"),
    entidadeTipo: text("entidade_tipo"),
    entidadeCnpj: text("entidade_cnpj"),
    unidadeFilial: text("unidade_filial"),
    profissionalSolicitante: text("profissional_solicitante"),
    solicitanteSetor: text("solicitante_setor"),
    solicitanteEmail: text("solicitante_email"),
    solicitanteTelefone: text("solicitante_telefone"),
    solicitadoEm: date("solicitado_em"),
    prazoSolicitado: date("prazo_solicitado"),
    numeroSinistro: text("numero_sinistro"),
    numeroApolice: text("numero_apolice"),
    seguradoNome: text("segurado_nome"),
    seguradoDocumento: text("segurado_documento"),
    vigenciaInicio: date("vigencia_inicio"),
    vigenciaFim: date("vigencia_fim"),
    coberturaAcionada: text("cobertura_acionada"),
    franquiaParticipacao: numeric("franquia_participacao", {
      precision: 12,
      scale: 2,
    }),
    regulacaoStatus: text("regulacao_status"),
    valorReclamado: numeric("valor_reclamado", { precision: 12, scale: 2 }),
    eventoHorario: time("evento_horario"),
    eventoMunicipio: text("evento_municipio"),
    eventoUf: char("evento_uf", { length: 2 }),
    eventoEndereco: text("evento_endereco"),
    eventoLocalizacao: text("evento_localizacao"),
    eventoOrgaoRegistro: text("evento_orgao_registro"),
    eventoNaturezas: jsonb("evento_naturezas").$type<string[]>(),
    descricaoEvento: text("descricao_evento"),
    veiculosEnvolvidos: text("veiculos_envolvidos"),
    bensTerceirosEnvolvidos: text("bens_terceiros_envolvidos"),
    contatoAcessoNome: text("contato_acesso_nome"),
    contatoAcessoTelefone: text("contato_acesso_telefone"),
    canalEntrega: text("canal_entrega"),
    documentosIniciaisReferencia: text("documentos_iniciais_referencia"),
    responsavelInformacoesNome: text("responsavel_informacoes_nome"),
    responsavelInformacoesCargo: text("responsavel_informacoes_cargo"),
    autorizadoEnvio: boolean("autorizado_envio"),
    formalizacao: jsonb("formalizacao").$type<import("./pre-analise/formalization").FormalizationSummary>(),
    formalizacaoVersao: integer("formalizacao_versao").notNull().default(0),
    arquivadoEm: timestamp("arquivado_em", { withTimezone: true }),
    criadoEm: timestamp("criado_em", { withTimezone: true })
      .notNull()
      .defaultNow(),
    atualizadoEm: timestamp("atualizado_em", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (table) => [
    check(
      "remonta_dossies_objeto_tipo_check",
      sql`${table.objetoTipo} IS NULL OR ${table.objetoTipo} IN ('veiculo', 'imovel', 'outro')`,
    ),
  ],
);

export const remontaDossiePreAnalises = pgTable("remonta_dossie_pre_analises", {
  id: uuid("id").primaryKey().defaultRandom(),
  dossieId: uuid("dossie_id").notNull().unique().references(() => remontaDossiesSinistro.id, { onDelete: "cascade" }),
  status: text("status").notNull().default("em_estudo").$type<"em_estudo" | "pendencia" | "concluida">(),
  dadosEstudados: text("dados_estudados").notNull().default(""),
  documentosConsultados: text("documentos_consultados").notNull().default(""),
  limitacoes: text("limitacoes").notNull().default(""),
  conclusao: text("conclusao").notNull().default(""),
  recomendacoes: jsonb("recomendacoes").notNull().default([]).$type<import("./pre-analise/workflow").InitialStudyInput["recomendacoes"]>(),
  versao: integer("versao").notNull().default(1),
  elaboradoPor: uuid("elaborado_por").notNull().references(() => userProfiles.id),
  concluidoEm: timestamp("concluido_em", { withTimezone: true }),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true }).notNull().defaultNow(),
});

export const remontaOrdensServico = pgTable(
  "remonta_ordens_servico",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    dossieId: uuid("dossie_id")
      .notNull()
      .references(() => remontaDossiesSinistro.id, { onDelete: "cascade" }),
    tipo: remontaOrdemServicoTipoEnum("tipo").notNull(),
    status: remontaOrdemServicoStatusEnum("status")
      .notNull()
      .default("rascunho"),
    responsavelId: uuid("responsavel_id"),
    resultado: text("resultado").$type<
      | "apto_para_execucao"
      | "pendencia_documental"
      | "inviavel_tecnicamente"
      | "fora_do_escopo_contratado"
    >(),
    justificativaResultado: text("justificativa_resultado"),
    motivoCancelamento: text("motivo_cancelamento"),
    politicaVersao: text("politica_versao").notNull().default("legado"),
    canalEnvio: text("canal_envio"),
    canalEntrega: text("canal_entrega"),
    prazoDocumentosEm: date("prazo_documentos_em"),
    prazoEntregaEm: date("prazo_entrega_em"),
    // F01: dados do atendimento técnico informados pelo cliente (migration 20261012000400).
    numeroOrdemExterna: text("numero_ordem_externa"),
    reguladorExterno: text("regulador_externo"),
    vistoriaPrevistaEm: date("vistoria_prevista_em"),
    solicitadoEm: timestamp("solicitado_em", { withTimezone: true }),
    iniciadoEm: timestamp("iniciado_em", { withTimezone: true }),
    concluidoEm: timestamp("concluido_em", { withTimezone: true }),
    canceladoEm: timestamp("cancelado_em", { withTimezone: true }),
    versao: integer("versao").notNull().default(1),
    criadoPor: uuid("criado_por").notNull(),
    criadoEm: timestamp("criado_em", { withTimezone: true })
      .notNull()
      .defaultNow(),
    atualizadoEm: timestamp("atualizado_em", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (table) => [
    unique("remonta_ordens_dossie_tipo_unique").on(
      table.dossieId,
      table.tipo,
    ),
    unique("remonta_order_dossier_pair").on(table.id, table.dossieId),
    uniqueIndex("remonta_ordens_dossie_ativa_uidx")
      .on(table.dossieId)
      .where(sql`${table.status} NOT IN ('concluido', 'cancelado')`),
    check(
      "remonta_ordens_resultado_check",
      sql`${table.resultado} IS NULL OR ${table.resultado} IN ('apto_para_execucao', 'pendencia_documental', 'inviavel_tecnicamente', 'fora_do_escopo_contratado')`,
    ),
  ],
);

export const remontaAnaliseRevisoes = pgTable("remonta_analise_revisoes", {
  id: uuid("id").primaryKey().defaultRandom(),
  dossieId: uuid("dossie_id").notNull().references(() => remontaDossiesSinistro.id, { onDelete: "cascade" }),
  ordemServicoId: uuid("ordem_servico_id").references(() => remontaOrdensServico.id, { onDelete: "cascade" }),
  etapa: text("etapa").$type<"inicial" | "conferencia">().notNull(),
  versao: integer("versao").notNull(),
  snapshot: jsonb("snapshot").$type<Record<string, unknown>>().notNull(),
  autorId: uuid("autor_id").notNull().references(() => userProfiles.id),
  criadoEm: timestamp("criado_em", { withTimezone: true }).notNull().defaultNow(),
}, (table) => [index("remonta_analise_revisoes_dossie_idx").on(table.dossieId),
  check("remonta_analise_revisoes_etapa_check", sql`${table.etapa} IN ('inicial','conferencia')`)]);

export const remontaOrdemChecklists = pgTable(
  "remonta_ordem_checklists",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    ordemServicoId: uuid("ordem_servico_id")
      .notNull()
      .references(() => remontaOrdensServico.id, { onDelete: "cascade" }),
    etapa: remontaChecklistEtapaEnum("etapa").notNull(),
    codigo: text("codigo").notNull(),
    versaoModelo: text("versao_modelo").notNull(),
    status: text("status")
      .$type<"rascunho" | "em_andamento" | "concluido">()
      .notNull()
      .default("rascunho"),
    responsavelId: uuid("responsavel_id"),
    conclusao: jsonb("conclusao"),
    iniciadoEm: timestamp("iniciado_em", { withTimezone: true }),
    concluidoEm: timestamp("concluido_em", { withTimezone: true }),
    criadoEm: timestamp("criado_em", { withTimezone: true })
      .notNull()
      .defaultNow(),
    atualizadoEm: timestamp("atualizado_em", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (table) => [
    unique("remonta_ordem_checklists_ordem_etapa_unique").on(
      table.ordemServicoId,
      table.etapa,
    ),
    check(
      "remonta_ordem_checklists_status_check",
      sql`${table.status} IN ('rascunho', 'em_andamento', 'concluido')`,
    ),
  ],
);

export const remontaOrdemChecklistItens = pgTable(
  "remonta_ordem_checklist_itens",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    checklistId: uuid("checklist_id")
      .notNull()
      .references(() => remontaOrdemChecklists.id, { onDelete: "cascade" }),
    codigo: text("codigo").notNull(),
    codigoOrigem: text("codigo_origem"),
    numero: integer("numero").notNull(),
    secao: text("secao").notNull(),
    categoria: text("categoria").notNull().default(""),
    descricao: text("descricao").notNull(),
    orientacao: text("orientacao"),
    obrigatorio: boolean("obrigatorio").notNull().default(false),
    respostaExterna: text("resposta_externa").$type<
      "encaminhado" | "indisponivel" | "nao_se_aplica"
    >(),
    justificativaExterna: text("justificativa_externa"),
    respondidoPor: uuid("respondido_por"),
    respondidoEm: timestamp("respondido_em", { withTimezone: true }),
    statusInterno: text("status_interno")
      .$type<
        "nao_solicitado" | "solicitado" | "recebido" | "nao_se_aplica"
      >()
      .notNull()
      .default("nao_solicitado"),
    justificativaInterna: text("justificativa_interna"),
    observacaoTecnica: text("observacao_tecnica"),
    origemEvidencia: jsonb("origem_evidencia").$type<import("./pre-analise/document-remittance").EvidenceOrigin>(),
    politicaItem: jsonb("politica_item").$type<import("./pre-analise/document-remittance").ItemPolicy>(),
    analisadoPor: uuid("analisado_por"),
    analisadoEm: timestamp("analisado_em", { withTimezone: true }),
    versao: integer("versao").notNull().default(1),
    criadoEm: timestamp("criado_em", { withTimezone: true })
      .notNull()
      .defaultNow(),
    atualizadoEm: timestamp("atualizado_em", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (table) => [
    unique("remonta_ordem_itens_checklist_codigo_unique").on(
      table.checklistId,
      table.codigo,
    ),
    check(
      "remonta_ordem_itens_resposta_externa_check",
      sql`${table.respostaExterna} IS NULL OR ${table.respostaExterna} IN ('encaminhado', 'indisponivel', 'nao_se_aplica')`,
    ),
    check(
      "remonta_ordem_itens_status_interno_check",
      sql`${table.statusInterno} IN ('nao_solicitado', 'solicitado', 'recebido', 'nao_se_aplica')`,
    ),
  ],
);

export const remontaOrdemPendencias = pgTable(
  "remonta_ordem_pendencias",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    ordemServicoId: uuid("ordem_servico_id")
      .notNull()
      .references(() => remontaOrdensServico.id, { onDelete: "cascade" }),
    checklistItemId: uuid("checklist_item_id").references(
      () => remontaOrdemChecklistItens.id,
      { onDelete: "set null" },
    ),
    descricao: text("descricao").notNull(),
    responsavelId: uuid("responsavel_id"),
    prazo: date("prazo"),
    status: text("status")
      .$type<"aberta" | "resolvida" | "cancelada">()
      .notNull()
      .default("aberta"),
    resposta: text("resposta"),
    criadoPor: uuid("criado_por").notNull(),
    abertaEm: timestamp("aberta_em", { withTimezone: true })
      .notNull()
      .defaultNow(),
    resolvidaPor: uuid("resolvida_por"),
    resolvidaEm: timestamp("resolvida_em", { withTimezone: true }),
    atualizadoEm: timestamp("atualizado_em", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (table) => [
    check(
      "remonta_ordem_pendencias_status_check",
      sql`${table.status} IN ('aberta', 'resolvida', 'cancelada')`,
    ),
    index("remonta_ordem_pendencias_ordem_servico_idx").on(table.ordemServicoId),
    index("remonta_ordem_pendencias_checklist_item_idx").on(table.checklistItemId),
  ],
);

export const remontaOrdemEntregas = pgTable("remonta_ordem_entregas", {
  id: uuid("id").primaryKey().defaultRandom(),
  ordemServicoId: uuid("ordem_servico_id").notNull().unique().references(() => remontaOrdensServico.id, { onDelete: "cascade" }),
  conteudo: jsonb("conteudo").notNull().$type<import("./pre-analise/workflow").TechnicalDeliveryInput>(),
  versao: integer("versao").notNull().default(1),
  autorId: uuid("autor_id").notNull().references(() => userProfiles.id),
  revisadoPor: uuid("revisado_por").references(() => userProfiles.id),
  revisaoJustificativa: text("revisao_justificativa"),
  publicadoEm: timestamp("publicado_em", { withTimezone: true }),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true }).notNull().defaultNow(),
});

export const remontaOrdemEntregaRevisoes = pgTable("remonta_ordem_entrega_revisoes", {
  id: uuid("id").primaryKey().defaultRandom(),
  ordemServicoId: uuid("ordem_servico_id").notNull().references(() => remontaOrdensServico.id, { onDelete: "cascade" }),
  versao: integer("versao").notNull(),
  snapshot: jsonb("snapshot").notNull(),
  autorId: uuid("autor_id").notNull().references(() => userProfiles.id),
  criadoEm: timestamp("criado_em", { withTimezone: true }).notNull().defaultNow(),
}, (table) => [unique("remonta_entrega_revisoes_ordem_versao_unique").on(table.ordemServicoId, table.versao)]);

export const remontaDossieArquivos = pgTable(
  "remonta_dossie_arquivos",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    dossieId: uuid("dossie_id")
      .notNull()
      .references(() => remontaDossiesSinistro.id, { onDelete: "cascade" }),
    ordemServicoId: uuid("ordem_servico_id").references(
      () => remontaOrdensServico.id,
      { onDelete: "set null" },
    ),
    checklistItemId: uuid("checklist_item_id").references(
      () => remontaOrdemChecklistItens.id,
      { onDelete: "set null" },
    ),
    pendenciaId: uuid("pendencia_id").references(
      () => remontaOrdemPendencias.id,
      { onDelete: "set null" },
    ),
    tipo: text("tipo").notNull(),
    storagePath: text("storage_path").notNull().unique(),
    storageUrl: text("storage_url").notNull(),
    hashIntegridade: text("hash_integridade"),
    criadoPor: uuid("criado_por")
      .notNull()
      .references(() => userProfiles.id),
    metadata: jsonb("metadata").notNull().default({}),
    criadoEm: timestamp("criado_em", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
  (table) => [
    index("remonta_dossie_arquivos_ordem_servico_idx").on(table.ordemServicoId),
    index("remonta_dossie_arquivos_checklist_item_idx").on(table.checklistItemId),
    index("remonta_dossie_arquivos_pendencia_idx").on(table.pendenciaId),
  ],
);

export const remontaLaudos = pgTable("remonta_laudos", {
  id: uuid("id").primaryKey().defaultRandom(),
  veiculoId: uuid("veiculo_id").references(() => veiculos.id, {
    onDelete: "set null",
  }),
  rtId: uuid("rt_id"), // plain uuid — logically references user_profiles.id (D-10)
  artId: uuid("art_id"), // plain uuid — column kept, FK removed (D-09)
  empresaId: uuid("empresa_id")
    .notNull()
    .references(() => empresas.id),
  parceiroId: uuid("parceiro_id").references(() => empresas.id, {
    onDelete: "set null",
  }),
  codigo: text("codigo").unique(),
  pdfUrl: text("pdf_url"),
  status: remontaLaudoStatusEnum("status").notNull().default("rascunho"),
  tipo: remontaLaudoTipoEnum("tipo").notNull().default("reclassificacao"),
  valorDesejado: numeric("valor_desejado", { precision: 12, scale: 2 }),
  classificacaoMonta: remontaClassificacaoMontaEnum("classificacao_monta"),
  conclusao: text("conclusao"), // "recuperavel" | "irrecuperavel"
  justificativa: text("justificativa"),
  nexoNarrativa: text("nexo_narrativa"),
  nexoDinamica: text("nexo_dinamica"),
  nexoGeometria: text("nexo_geometria"),
  nexoIndicativos: text("nexo_indicativos"),
  nexoClassificacao: remontaNexoCausalClassificacaoEnum("nexo_classificacao"),
  dossieId: uuid("dossie_id").references(() => remontaDossiesSinistro.id, {
    onDelete: "restrict",
  }),
  ordemServicoId: uuid("ordem_servico_id").references(() => remontaOrdensServico.id, { onDelete: "restrict" }),
  anexoAplicavel: smallint("anexo_aplicavel"),
  revisao: integer("revisao").notNull().default(1),
  emitidoEm: timestamp("emitido_em", { withTimezone: true }),
  criadoPor: uuid("criado_por").references(() => userProfiles.id),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
}, table => [
  unique("remonta_laudos_signature_identity").on(table.id, table.rtId, table.empresaId),
]);

// Public authenticity is projected by the backend; certificate secrets never belong here.
export const remontaLaudoAssinaturas = pgTable("remonta_laudo_assinaturas", {
  id: uuid("id").primaryKey().defaultRandom(),
  laudoId: uuid("laudo_id").notNull().references(() => remontaLaudos.id, { onDelete: "restrict" }),
  revisao: integer("revisao").notNull(),
  rtId: uuid("rt_id").notNull().references(() => userProfiles.id, { onDelete: "restrict" }),
  empresaId: uuid("empresa_id").notNull().references(() => empresas.id, { onDelete: "restrict" }),
  tokenHash: text("token_hash").notNull().unique(),
  pdfSha256: text("pdf_sha256").notNull(),
  storagePath: text("storage_path").notNull().unique(),
  signerName: text("signer_name").notNull(),
  issuer: text("issuer").notNull(),
  serial: text("serial").notNull(),
  certificateFingerprintSha256: text("certificate_fingerprint_sha256").notNull(),
  validFrom: timestamp("valid_from", { withTimezone: true }).notNull(),
  validTo: timestamp("valid_to", { withTimezone: true }).notNull(),
  signedAt: timestamp("signed_at", { withTimezone: true }).notNull(),
  policyOid: text("policy_oid").notNull(),
  revocationCheckedAt: timestamp("revocation_checked_at", { withTimezone: true }).notNull(),
  padrao: text("padrao").notNull().$type<"CMS-detached-SHA256">(),
  criadoEm: timestamp("criado_em", { withTimezone: true }).notNull().defaultNow(),
}, table => [
  foreignKey({ name: "remonta_assinatura_laudo_scope", columns: [table.laudoId, table.rtId, table.empresaId], foreignColumns: [remontaLaudos.id, remontaLaudos.rtId, remontaLaudos.empresaId] }).onDelete("restrict"),
  unique("remonta_assinatura_artifact_unique").on(table.laudoId, table.pdfSha256),
  index("remonta_assinatura_revision_idx").on(table.laudoId, table.revisao),
  index("remonta_assinatura_rt_idx").on(table.rtId),
  index("remonta_assinatura_empresa_idx").on(table.empresaId),
  check("remonta_assinatura_revision", sql`${table.revisao}>0`),
  check("remonta_assinatura_hashes", sql`${table.tokenHash} ~ '^[0-9a-f]{64}$' AND ${table.pdfSha256} ~ '^[0-9a-f]{64}$' AND ${table.certificateFingerprintSha256} ~ '^[0-9a-f]{64}$'`),
  check("remonta_assinatura_validity", sql`isfinite(${table.validFrom}) AND isfinite(${table.validTo}) AND isfinite(${table.signedAt}) AND ${table.validFrom}<${table.validTo} AND ${table.signedAt} BETWEEN ${table.validFrom} AND ${table.validTo}`),
  check("remonta_assinatura_revocation", sql`isfinite(${table.revocationCheckedAt}) AND ${table.revocationCheckedAt}<=${table.signedAt}`),
  check("remonta_assinatura_standard", sql`${table.padrao}='CMS-detached-SHA256'`),
  check("remonta_assinatura_signer", sql`length(btrim(${table.signerName})) BETWEEN 1 AND 300`),
  check("remonta_assinatura_issuer", sql`length(btrim(${table.issuer})) BETWEEN 1 AND 1000`),
  check("remonta_assinatura_serial", sql`length(${table.serial}) BETWEEN 1 AND 256 AND ${table.serial} ~ '^[0-9a-fA-F:]+$'`),
  check("remonta_assinatura_policy", sql`length(${table.policyOid})<=200 AND ${table.policyOid} ~ '^[0-9]+([.][0-9]+)+$'`),
  check("remonta_assinatura_path", sql`${table.storagePath} ~ ('^laudos/' || ${table.empresaId}::text || '/' || ${table.laudoId}::text || '/[A-Za-z0-9_-]+[.]pdf$')`),
]);
export type RemontaLaudoAssinatura = typeof remontaLaudoAssinaturas.$inferSelect;
export type InsertRemontaLaudoAssinatura = typeof remontaLaudoAssinaturas.$inferInsert;

export const remontaCategoriasEstruturais = pgTable(
  "remonta_categorias_estruturais",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    nome: text("nome").notNull(),
    tipoVeiculo: tipoVeiculoEnum("tipo_veiculo").notNull(),
    descricao: text("descricao"),
    sequencia: smallint("sequencia").notNull().default(0),
    uf: char("uf", { length: 2 }).notNull().default("MG"),
    ativo: boolean("ativo").notNull().default(true),
    criadoEm: timestamp("criado_em", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
);

export const remontaElementosEstruturais = pgTable(
  "remonta_elementos_estruturais",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    categoriaId: uuid("categoria_id")
      .notNull()
      .references(() => remontaCategoriasEstruturais.id, {
        onDelete: "cascade",
      }),
    tipoVeiculo: tipoVeiculoEnum("tipo_veiculo").notNull(),
    nome: text("nome").notNull(),
    descricao: text("descricao"),
    sequencia: smallint("sequencia").notNull().default(0),
    uf: char("uf", { length: 2 }).notNull().default("MG"),
    anexo: smallint("anexo"),
    numeroItem: smallint("numero_item"),
    gravidade: char("gravidade", { length: 1 }),
    ativo: boolean("ativo").notNull().default(true),
    criadoEm: timestamp("criado_em", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
);

export const remontaItensLaudo = pgTable("remonta_itens_laudo", {
  id: uuid("id").primaryKey().defaultRandom(),
  laudoId: uuid("laudo_id")
    .notNull()
    .references(() => remontaLaudos.id, { onDelete: "cascade" }),
  elementoId: uuid("elemento_id")
    .notNull()
    .references(() => remontaElementosEstruturais.id, { onDelete: "restrict" }),
  status: remontaElementoStatusEnum("status").notNull().default("nao_avaliado"),
  observacao: text("observacao"),
  fotoUrl: text("foto_url"),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
  atualizadoEm: timestamp("atualizado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

export const remontaFotos = pgTable("remonta_fotos", {
  id: uuid("id").primaryKey().defaultRandom(),
  laudoId: uuid("laudo_id")
    .notNull()
    .references(() => remontaLaudos.id, { onDelete: "cascade" }),
  storageUrl: text("storage_url").notNull(),
  storagePath: text("storage_path").notNull(),
  elementoId: uuid("elemento_id"),
  tipoFoto: remontaTipoFotoEnum("tipo_foto"),
  hashIntegridade: text("hash_integridade"),
  criadoPor: uuid("criado_por"), // plain uuid — logically references user_profiles.id (D-10)
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

export const remontaReparos = pgTable("remonta_reparos", {
  id: uuid("id").primaryKey().defaultRandom(),
  laudoId: uuid("laudo_id")
    .notNull()
    .references(() => remontaLaudos.id, { onDelete: "cascade" }),
  descricao: text("descricao").notNull(),
  numeroNf: text("numero_nf").notNull(),
  valor: numeric("valor", { precision: 10, scale: 2 }).notNull(),
  fornecedor: text("fornecedor").notNull(),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

export const remontaOcorrencias = pgTable("remonta_ocorrencias", {
  id: uuid("id").primaryKey().defaultRandom(),
  dossieId: uuid("dossie_id")
    .notNull()
    .references(() => remontaDossiesSinistro.id, { onDelete: "cascade" }),
  laudoId: uuid("laudo_id").references(() => remontaLaudos.id, {
    onDelete: "set null",
  }),
  tipo: remontaOcorrenciaTipoEnum("tipo").notNull(),
  descricao: text("descricao").notNull(),
  decisao: text("decisao"),
  autorId: uuid("autor_id").notNull(), // plain uuid — logicamente referencia user_profiles.id (D-10)
  fotoUrl: text("foto_url"),
  referenciaOcorrenciaId: uuid("referencia_ocorrencia_id"),
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
});

export const remontaOrcamentosItem = pgTable("remonta_orcamentos_item", {
  ordemServicoId: uuid("ordem_servico_id").references(() => remontaOrdensServico.id, { onDelete: "restrict" }),
  id: uuid("id").primaryKey().defaultRandom(),
  dossieId: uuid("dossie_id")
    .notNull()
    .references(() => remontaDossiesSinistro.id, { onDelete: "cascade" }),
  categoria: remontaOrcamentoCategoriaEnum("categoria").notNull(),
  descricao: text("descricao").notNull(),
  valorUnitario: numeric("valor_unitario", { precision: 12, scale: 2 }).notNull(),
  quantidade: numeric("quantidade", { precision: 10, scale: 2 })
    .notNull()
    .default("1"),
  status: remontaOrcamentoItemStatusEnum("status").notNull().default("proposto"),
  justificativa: text("justificativa"),
  valorAprovado: numeric("valor_aprovado", { precision: 12, scale: 2 }),
  motivoDecisao: text("motivo_decisao"),
  criadoPor: uuid("criado_por").notNull(), // plain uuid — logicamente referencia user_profiles.id (D-10)
  criadoEm: timestamp("criado_em", { withTimezone: true })
    .notNull()
    .defaultNow(),
  decididoPor: uuid("decidido_por"),
  decididoEm: timestamp("decidido_em", { withTimezone: true }),
});

export const remontaSinistroLocalizacoes = pgTable(
  "remonta_sinistro_localizacoes",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    veiculoId: uuid("veiculo_id")
      .notNull()
      .unique()
      .references(() => veiculos.id),
    endereco: text("endereco").notNull(),
    latitude: numeric("latitude", { precision: 10, scale: 7 }).notNull(),
    longitude: numeric("longitude", { precision: 10, scale: 7 }).notNull(),
    criadoEm: timestamp("criado_em", { withTimezone: true })
      .notNull()
      .defaultNow(),
    atualizadoEm: timestamp("atualizado_em", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
);

export const remontaDesmontesSugeridos = pgTable(
  "remonta_desmontes_sugeridos",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    sinistroLocalizacaoId: uuid("sinistro_localizacao_id")
      .notNull()
      .references(() => remontaSinistroLocalizacoes.id, {
        onDelete: "cascade",
      }),
    nome: text("nome").notNull(),
    telefone: text("telefone"),
    endereco: text("endereco").notNull(),
    latitude: numeric("latitude", { precision: 10, scale: 7 }).notNull(),
    longitude: numeric("longitude", { precision: 10, scale: 7 }).notNull(),
    distanciaKm: numeric("distancia_km", { precision: 6, scale: 2 }).notNull(),
    googlePlaceId: text("google_place_id"),
    criadoEm: timestamp("criado_em", { withTimezone: true })
      .notNull()
      .defaultNow(),
  },
);

// ---------------------------------------------------------------------------
// Relations — shared tables
// ---------------------------------------------------------------------------

export const empresasRelations = relations(empresas, ({ one, many }) => ({
  vistoriadora: one(empresas, {
    fields: [empresas.vistoriadoraId],
    references: [empresas.id],
    relationName: "vistoriadoraParceiros",
  }),
  parceiros: many(empresas, { relationName: "vistoriadoraParceiros" }),
  usuarios: many(userProfiles),
  inviteCodes: many(inviteCodes),
  veiculos: many(veiculos),
  remontaLaudos: many(remontaLaudos, { relationName: "empresaLaudos" }),
  parceiroLaudos: many(remontaLaudos, { relationName: "parceiroLaudos" }),
}));

export const userProfilesRelations = relations(userProfiles, ({ one }) => ({
  empresa: one(empresas, {
    fields: [userProfiles.empresaId],
    references: [empresas.id],
  }),
}));

export const remontaSolicitantePermissoesRelations = relations(
  remontaSolicitantePermissoes,
  ({ one }) => ({
    solicitante: one(empresas, {
      fields: [remontaSolicitantePermissoes.solicitanteId],
      references: [empresas.id],
      relationName: "solicitantePermissoes",
    }),
    vistoriadora: one(empresas, {
      fields: [remontaSolicitantePermissoes.vistoriadoraId],
      references: [empresas.id],
      relationName: "vistoriadoraPermissoes",
    }),
    concedente: one(userProfiles, {
      fields: [remontaSolicitantePermissoes.concedidoPor],
      references: [userProfiles.id],
    }),
  }),
);

export const remontaDossiesSinistroRelations = relations(
  remontaDossiesSinistro,
  ({ one, many }) => ({
    veiculo: one(veiculos, {
      fields: [remontaDossiesSinistro.veiculoId],
      references: [veiculos.id],
    }),
    solicitante: one(empresas, {
      fields: [remontaDossiesSinistro.solicitanteId],
      references: [empresas.id],
      relationName: "solicitanteDossies",
    }),
    vistoriadora: one(empresas, {
      fields: [remontaDossiesSinistro.vistoriadoraId],
      references: [empresas.id],
      relationName: "vistoriadoraDossies",
    }),
    criador: one(userProfiles, {
      fields: [remontaDossiesSinistro.criadoPor],
      references: [userProfiles.id],
    }),
    arquivos: many(remontaDossieArquivos),
    ordensServico: many(remontaOrdensServico),
    laudos: many(remontaLaudos),
  }),
);

export const remontaOrdensServicoRelations = relations(
  remontaOrdensServico,
  ({ one, many }) => ({
    dossie: one(remontaDossiesSinistro, {
      fields: [remontaOrdensServico.dossieId],
      references: [remontaDossiesSinistro.id],
    }),
    checklists: many(remontaOrdemChecklists),
    pendencias: many(remontaOrdemPendencias),
    arquivos: many(remontaDossieArquivos),
  }),
);

export const remontaOrdemChecklistsRelations = relations(
  remontaOrdemChecklists,
  ({ one, many }) => ({
    ordemServico: one(remontaOrdensServico, {
      fields: [remontaOrdemChecklists.ordemServicoId],
      references: [remontaOrdensServico.id],
    }),
    itens: many(remontaOrdemChecklistItens),
  }),
);

export const remontaOrdemChecklistItensRelations = relations(
  remontaOrdemChecklistItens,
  ({ one, many }) => ({
    checklist: one(remontaOrdemChecklists, {
      fields: [remontaOrdemChecklistItens.checklistId],
      references: [remontaOrdemChecklists.id],
    }),
    pendencias: many(remontaOrdemPendencias),
    arquivos: many(remontaDossieArquivos),
  }),
);

export const remontaOrdemPendenciasRelations = relations(
  remontaOrdemPendencias,
  ({ one, many }) => ({
    ordemServico: one(remontaOrdensServico, {
      fields: [remontaOrdemPendencias.ordemServicoId],
      references: [remontaOrdensServico.id],
    }),
    checklistItem: one(remontaOrdemChecklistItens, {
      fields: [remontaOrdemPendencias.checklistItemId],
      references: [remontaOrdemChecklistItens.id],
    }),
    arquivos: many(remontaDossieArquivos),
  }),
);

export const remontaDossieArquivosRelations = relations(
  remontaDossieArquivos,
  ({ one }) => ({
    dossie: one(remontaDossiesSinistro, {
      fields: [remontaDossieArquivos.dossieId],
      references: [remontaDossiesSinistro.id],
    }),
    criador: one(userProfiles, {
      fields: [remontaDossieArquivos.criadoPor],
      references: [userProfiles.id],
    }),
    ordemServico: one(remontaOrdensServico, {
      fields: [remontaDossieArquivos.ordemServicoId],
      references: [remontaOrdensServico.id],
    }),
    checklistItem: one(remontaOrdemChecklistItens, {
      fields: [remontaDossieArquivos.checklistItemId],
      references: [remontaOrdemChecklistItens.id],
    }),
    pendencia: one(remontaOrdemPendencias, {
      fields: [remontaDossieArquivos.pendenciaId],
      references: [remontaOrdemPendencias.id],
    }),
  }),
);

export const inviteCodesRelations = relations(inviteCodes, ({ one }) => ({
  vistoriadora: one(empresas, {
    fields: [inviteCodes.vistoriadoraId],
    references: [empresas.id],
  }),
}));

export const veiculosRelations = relations(veiculos, ({ one, many }) => ({
  empresa: one(empresas, {
    fields: [veiculos.parceiroId],
    references: [empresas.id],
  }),
  remontaLaudos: many(remontaLaudos),
}));

// ---------------------------------------------------------------------------
// Relations — ReMonta domain tables
// ---------------------------------------------------------------------------


export const remontaArtsRelations = relations(remontaArts, ({ one }) => ({
  rt: one(userProfiles, {
    fields: [remontaArts.rtId],
    references: [userProfiles.id],
  }),
}));

export const remontaSinistroLocalizacoesRelations = relations(
  remontaSinistroLocalizacoes,
  ({ one, many }) => ({
    veiculo: one(veiculos, {
      fields: [remontaSinistroLocalizacoes.veiculoId],
      references: [veiculos.id],
    }),
    desmontes: many(remontaDesmontesSugeridos),
  }),
);

export const remontaDesmontesSugeridosRelations = relations(
  remontaDesmontesSugeridos,
  ({ one }) => ({
    sinistroLocalizacao: one(remontaSinistroLocalizacoes, {
      fields: [remontaDesmontesSugeridos.sinistroLocalizacaoId],
      references: [remontaSinistroLocalizacoes.id],
    }),
  }),
);

export const remontaLaudosRelations = relations(
  remontaLaudos,
  ({ one, many }) => ({
    veiculo: one(veiculos, {
      fields: [remontaLaudos.veiculoId],
      references: [veiculos.id],
    }),
    rt: one(userProfiles, {
      fields: [remontaLaudos.rtId],
      references: [userProfiles.id],
    }),
    art: one(remontaArts, {
      fields: [remontaLaudos.artId],
      references: [remontaArts.id],
    }),
    empresa: one(empresas, {
      fields: [remontaLaudos.empresaId],
      references: [empresas.id],
      relationName: "empresaLaudos",
    }),
    parceiro: one(empresas, {
      fields: [remontaLaudos.parceiroId],
      references: [empresas.id],
      relationName: "parceiroLaudos",
    }),
    dossie: one(remontaDossiesSinistro, {
      fields: [remontaLaudos.dossieId],
      references: [remontaDossiesSinistro.id],
    }),
    criadoPorUser: one(userProfiles, {
      fields: [remontaLaudos.criadoPor],
      references: [userProfiles.id],
    }),
    itens: many(remontaItensLaudo),
    fotos: many(remontaFotos),
    reparos: many(remontaReparos),
  }),
);

export const remontaCategoriasEstruturaisRelations = relations(
  remontaCategoriasEstruturais,
  ({ many }) => ({
    elementos: many(remontaElementosEstruturais),
  }),
);

export const remontaElementosEstruturaisRelations = relations(
  remontaElementosEstruturais,
  ({ one, many }) => ({
    categoria: one(remontaCategoriasEstruturais, {
      fields: [remontaElementosEstruturais.categoriaId],
      references: [remontaCategoriasEstruturais.id],
    }),
    itensLaudo: many(remontaItensLaudo),
  }),
);

export const remontaItensLaudoRelations = relations(
  remontaItensLaudo,
  ({ one }) => ({
    laudo: one(remontaLaudos, {
      fields: [remontaItensLaudo.laudoId],
      references: [remontaLaudos.id],
    }),
    elemento: one(remontaElementosEstruturais, {
      fields: [remontaItensLaudo.elementoId],
      references: [remontaElementosEstruturais.id],
    }),
  }),
);

export const remontaFotosRelations = relations(remontaFotos, ({ one }) => ({
  laudo: one(remontaLaudos, {
    fields: [remontaFotos.laudoId],
    references: [remontaLaudos.id],
  }),
}));

export const remontaReparosRelations = relations(remontaReparos, ({ one }) => ({
  laudo: one(remontaLaudos, {
    fields: [remontaReparos.laudoId],
    references: [remontaLaudos.id],
  }),
}));

// ---------------------------------------------------------------------------
// SECTION 7 — Zod insert schemas
// ---------------------------------------------------------------------------

export const insertEmpresaSchema = createInsertSchema(empresas, {
  cidade: z.string().min(1),
}).omit({
  id: true,
  criadoEm: true,
  atualizadoEm: true,
});

export const insertUserProfileSchema = createInsertSchema(userProfiles).omit({
  criadoEm: true,
  atualizadoEm: true,
});

export const insertUserInviteSchema = createInsertSchema(userInvites).omit({
  id: true,
  criadoEm: true,
  usos: true,
});

export const insertInviteCodeSchema = createInsertSchema(inviteCodes).omit({
  id: true,
  criadoEm: true,
  usos: true,
});

const requiredVehicleText = (label: string) =>
  z.string({ required_error: `${label} obrigatório`, invalid_type_error: `${label} deve ser texto` }).trim().min(1, `${label} obrigatório`);

// The canonical SQL requires these fields even though legacy Drizzle metadata is nullable.
// Validate after normalization so punctuation-only chassis cannot become an empty value.
export const insertVeiculoSchema = createInsertSchema(veiculos, {
  placa: requiredVehicleText("Placa").transform(v => v.toUpperCase().replace(/\s/g, "")).pipe(z.string().min(1, "Placa obrigatória")),
  chassi: requiredVehicleText("Chassi").transform(v => v.toUpperCase().replace(/[^A-Z0-9]/g, "")).pipe(z.string().min(1, "Chassi obrigatório")),
  cor: requiredVehicleText("Cor"),
  marca: requiredVehicleText("Marca"),
  modelo: requiredVehicleText("Modelo"),
  parceiroId: z.string().uuid().optional(), // Authority is supplied by the authenticated route.
}).omit({
  id: true,
  criadoEm: true,
  atualizadoEm: true,
});

const batchVehicleYear = (value: unknown) =>
  value == null || (typeof value === "string" && !value.trim()) ? undefined : typeof value === "string" ? Number(value.trim()) : value;

/** CSV rows use `tipo` and string years; all required vehicle data shares the HTTP contract. */
export const veiculoBatchRowSchema = insertVeiculoSchema.pick({ placa: true, chassi: true, cor: true, marca: true, modelo: true }).extend({
  tipo: insertVeiculoSchema.shape.tipoVeiculo,
  numeroNota: requiredVehicleText("Número da nota"),
  anoFabricacao: z.preprocess(batchVehicleYear, insertVeiculoSchema.shape.anoFabricacao),
  anoModelo: z.preprocess(batchVehicleYear, insertVeiculoSchema.shape.anoModelo),
  classificacaoPorte: z.preprocess(v => v === "" || v == null ? undefined : v, insertVeiculoSchema.shape.classificacaoPorte),
  renavam: z.preprocess(v => v == null ? undefined : v, z.string().trim().transform(v => v.replace(/\D/g, "") || undefined).optional()),
  valorFipe: z.preprocess(v => v == null || v === "" ? undefined : v, z.union([z.string(), z.number().finite()]).transform(String).optional()),
});


export const insertRemontaArtSchema = createInsertSchema(remontaArts).omit({
  id: true,
  criadoEm: true,
  atualizadoEm: true,
});

export const insertRemontaSinistroLocalizacaoSchema = createInsertSchema(
  remontaSinistroLocalizacoes,
).omit({
  id: true,
  criadoEm: true,
  atualizadoEm: true,
});

export const insertRemontaDesmonteSugeridoSchema = createInsertSchema(
  remontaDesmontesSugeridos,
).omit({
  id: true,
  criadoEm: true,
});

export const insertRemontaSolicitantePermissaoSchema = createInsertSchema(
  remontaSolicitantePermissoes,
).omit({
  atualizadoEm: true,
});

export const insertRemontaDossieSinistroSchema = createInsertSchema(
  remontaDossiesSinistro,
  {
    objetoTipo: z.enum(["veiculo", "imovel", "outro"]).nullable().optional(),
  },
).omit({
  id: true,
  criadoEm: true,
  atualizadoEm: true,
});

export const insertRemontaDossieArquivoSchema = createInsertSchema(
  remontaDossieArquivos,
).omit({
  id: true,
  criadoEm: true,
});

export const insertRemontaLaudoSchema = createInsertSchema(remontaLaudos).omit({
  id: true,
  criadoEm: true,
  atualizadoEm: true,
});

export const insertRemontaCategoriaEstrutural = createInsertSchema(
  remontaCategoriasEstruturais,
).omit({
  id: true,
  criadoEm: true,
});

export const insertRemontaElementoEstrutural = createInsertSchema(
  remontaElementosEstruturais,
).omit({
  id: true,
  criadoEm: true,
});

export const insertRemontaItemLaudo = createInsertSchema(
  remontaItensLaudo,
).omit({
  id: true,
  criadoEm: true,
  atualizadoEm: true,
});

export const insertRemontaFotoSchema = createInsertSchema(remontaFotos).omit({
  id: true,
  criadoEm: true,
});

export const insertRemontaReparoSchema = createInsertSchema(
  remontaReparos,
).omit({
  id: true,
  criadoEm: true,
});

export const insertRemontaOcorrenciaSchema = createInsertSchema(
  remontaOcorrencias,
).omit({
  id: true,
  criadoEm: true,
});

export const insertRemontaOrcamentoItemSchema = createInsertSchema(
  remontaOrcamentosItem,
).omit({
  id: true,
  status: true,
  valorAprovado: true,
  motivoDecisao: true,
  criadoEm: true,
  decididoPor: true,
  decididoEm: true,
});

// ---------------------------------------------------------------------------
// Type exports — shared tables
// ---------------------------------------------------------------------------

export type Empresa = typeof empresas.$inferSelect;
export type UserProfile = typeof userProfiles.$inferSelect;
export type InviteCode = typeof inviteCodes.$inferSelect;
export type UserInvite = typeof userInvites.$inferSelect;
export type Veiculo = typeof veiculos.$inferSelect;

export type InsertEmpresa = z.infer<typeof insertEmpresaSchema>;
export type InsertUserProfile = z.infer<typeof insertUserProfileSchema>;
export type InsertInviteCode = z.infer<typeof insertInviteCodeSchema>;
export type InsertUserInvite = z.infer<typeof insertUserInviteSchema>;
export type InsertVeiculo = z.infer<typeof insertVeiculoSchema>;

// ---------------------------------------------------------------------------
// Type exports — ReMonta domain tables
// ---------------------------------------------------------------------------

export type RemontaArt = typeof remontaArts.$inferSelect;
export type RemontaSolicitantePermissao =
  typeof remontaSolicitantePermissoes.$inferSelect;
export type RemontaDossieSinistro = typeof remontaDossiesSinistro.$inferSelect;
export type RemontaDossieArquivo = typeof remontaDossieArquivos.$inferSelect;
export type RemontaOrdemServico = typeof remontaOrdensServico.$inferSelect;
export type RemontaOrdemChecklist = typeof remontaOrdemChecklists.$inferSelect;
export type RemontaOrdemChecklistItem =
  typeof remontaOrdemChecklistItens.$inferSelect;
export type RemontaOrdemPendencia = typeof remontaOrdemPendencias.$inferSelect;
export type RemontaLaudo = typeof remontaLaudos.$inferSelect;
export type RemontaCategoriaEstrutural =
  typeof remontaCategoriasEstruturais.$inferSelect;
export type RemontaElementoEstrutural =
  typeof remontaElementosEstruturais.$inferSelect;
export type RemontaItemLaudo = typeof remontaItensLaudo.$inferSelect;

export type InsertRemontaArt = z.infer<typeof insertRemontaArtSchema>;
export type InsertRemontaSolicitantePermissao = z.infer<
  typeof insertRemontaSolicitantePermissaoSchema
>;
export type InsertRemontaDossieSinistro = z.infer<
  typeof insertRemontaDossieSinistroSchema
>;
export type InsertRemontaDossieArquivo = z.infer<
  typeof insertRemontaDossieArquivoSchema
>;
export type InsertRemontaLaudo = z.infer<typeof insertRemontaLaudoSchema>;
export type InsertRemontaCategoriaEstrutural = z.infer<
  typeof insertRemontaCategoriaEstrutural
>;
export type InsertRemontaElementoEstrutural = z.infer<
  typeof insertRemontaElementoEstrutural
>;
export type InsertRemontaItemLaudo = z.infer<typeof insertRemontaItemLaudo>;

export type RemontaFoto = typeof remontaFotos.$inferSelect;
export type RemontaReparo = typeof remontaReparos.$inferSelect;
export type InsertRemontaFoto = z.infer<typeof insertRemontaFotoSchema>;
export type InsertRemontaReparo = z.infer<typeof insertRemontaReparoSchema>;

export type RemontaOcorrencia = typeof remontaOcorrencias.$inferSelect;
export type InsertRemontaOcorrencia = z.infer<
  typeof insertRemontaOcorrenciaSchema
>;

export type RemontaOrcamentoItem = typeof remontaOrcamentosItem.$inferSelect;
export type InsertRemontaOrcamentoItem = z.infer<
  typeof insertRemontaOrcamentoItemSchema
>;

export type RemontaSinistroLocalizacao =
  typeof remontaSinistroLocalizacoes.$inferSelect;
export type InsertRemontaSinistroLocalizacao = z.infer<
  typeof insertRemontaSinistroLocalizacaoSchema
>;
export type RemontaDesmonteSugerido =
  typeof remontaDesmontesSugeridos.$inferSelect;
export type InsertRemontaDesmonteSugerido = z.infer<
  typeof insertRemontaDesmonteSugeridoSchema
>;

// ---------------------------------------------------------------------------
// Shared constants
// ---------------------------------------------------------------------------

export const empresaTipos = [
  "OWNER",
  "VISTORIADORA",
  "PARCEIRO_DE_NEGOCIOS",
] as const;

export const tipoVeiculos = [
  "auto_leve",
  "moto",
  "caminhao",
  "onibus",
] as const;

export const userRoles = [
  "owner",
  "admin",
  "rt",
  "tv",
  "parceiro_negocios",
  "auditor",
] as const;

export const remontaLaudoStatuses = [
  "rascunho",
  "em_vistoria",
  "em_avaliacao_rt",
  "em_auditoria",
  "finalizado",
  "cancelado",
] as const;

export const remontaClassificacoesMonta = [
  "baixa_monta",
  "media_monta",
  "alta_monta",
  "remontagem_total",
  "pequena_monta",
  "grande_monta",
] as const;

export const remontaTiposFoto = [
  "veiculo_frente",
  "veiculo_traseira",
  "veiculo_lateral_dir",
  "veiculo_lateral_esq",
  "veiculo_interior",
  "veiculo_km",
  "veiculo_motor",
  "veiculo_identificacao",
  "elemento",
  "veiculo_45_dianteira_esq",
  "veiculo_45_dianteira_dir",
  "veiculo_45_traseira_esq",
  "veiculo_45_traseira_dir",
  "dano",
] as const;

export const remontaElementoStatuses = [
  "integro",
  "danificado",
  "substituido",
  "reparado",
  "inexistente",
  "nao_avaliado",
  "sim",
  "nao",
  "na",
] as const;

export const remontaDocumentoColetas = pgTable("remonta_documento_coletas", {
  id: uuid("id").primaryKey().defaultRandom(), dossieId: uuid("dossie_id").notNull().references(() => remontaDossiesSinistro.id), ordemServicoId: uuid("ordem_servico_id").references(() => remontaOrdensServico.id),
  etapa: text("etapa").notNull().$type<"inicial"|"especifica">(), requisitos: jsonb("requisitos").notNull().$type<import("./documentos-externos").DocumentalRequirement[]>(), politicaVersao: text("politica_versao").notNull().default("portal-v1"), mensagemPublica: text("mensagem_publica").notNull(), prazo: date("prazo"), estado: text("estado").notNull().default("aberta"), versao: integer("versao").notNull().default(1), criadoPor: uuid("criado_por").notNull().references(() => userProfiles.id), criadoEm: timestamp("criado_em", {withTimezone:true}).notNull().defaultNow(),
}, table => [
  unique().on(table.id, table.dossieId), unique().on(table.id, table.ordemServicoId),
  foreignKey({ columns: [table.ordemServicoId, table.dossieId], foreignColumns: [remontaOrdensServico.id, remontaOrdensServico.dossieId] }).onDelete("cascade"),
  check("remonta_collection_stage", sql`(${table.etapa}='inicial' AND ${table.ordemServicoId} IS NULL) OR (${table.etapa}='especifica' AND ${table.ordemServicoId} IS NOT NULL)`),
  index("remonta_coletas_dossie_idx").on(table.dossieId, table.criadoEm),
  index("remonta_coletas_order_idx").on(table.ordemServicoId), index("remonta_coletas_creator_idx").on(table.criadoPor),
]);
export const remontaDocumentoAcessos = pgTable("remonta_documento_acessos", {
 id: uuid("id").primaryKey().defaultRandom(), coletaId: uuid("coleta_id").notNull().references(() => remontaDocumentoColetas.id), email: text("email").notNull(), conviteHash: text("convite_hash").notNull().unique(), expiraEm: timestamp("expira_em",{withTimezone:true}).notNull(), revogadoEm: timestamp("revogado_em",{withTimezone:true}), codigoHash: text("codigo_hash"), codigoExpiraEm: timestamp("codigo_expira_em",{withTimezone:true}), codigoEnviadoEm: timestamp("codigo_enviado_em",{withTimezone:true}), codigoConsumidoEm: timestamp("codigo_consumido_em",{withTimezone:true}), tentativas: integer("tentativas").notNull().default(0), criadoPor: uuid("criado_por").notNull().references(() => userProfiles.id), criadoEm: timestamp("criado_em",{withTimezone:true}).notNull().defaultNow(),
}, table => [
  unique().on(table.id, table.coletaId),
  index("remonta_acessos_coleta_idx").on(table.coletaId, table.expiraEm),
  index("remonta_acessos_creator_idx").on(table.criadoPor), index("remonta_acessos_expiry_idx").on(table.expiraEm),
  check("remonta_access_attempts", sql`${table.tentativas} BETWEEN 0 AND 5`),
]);
export const remontaDocumentoSessoes = pgTable("remonta_documento_sessoes", {
 id: uuid("id").primaryKey().defaultRandom(), acessoId: uuid("acesso_id").notNull().references(() => remontaDocumentoAcessos.id), segredoHash: text("segredo_hash").notNull().unique(), csrfHash: text("csrf_hash").notNull(), expiraEm: timestamp("expira_em",{withTimezone:true}).notNull(), ultimaAtividadeEm: timestamp("ultima_atividade_em",{withTimezone:true}).notNull().defaultNow(), encerradaEm: timestamp("encerrada_em",{withTimezone:true}), criadoEm: timestamp("criado_em",{withTimezone:true}).notNull().defaultNow(),
}, table => [
  index("remonta_sessoes_acesso_idx").on(table.acessoId, table.expiraEm), index("remonta_sessoes_expiry_idx").on(table.expiraEm),
]);
export const remontaDocumentoArquivos = pgTable("remonta_documento_arquivos", {
 id: uuid("id").primaryKey().defaultRandom(), coletaId: uuid("coleta_id").notNull().references(() => remontaDocumentoColetas.id), acessoId: uuid("acesso_id").notNull().references(() => remontaDocumentoAcessos.id), remessaId: uuid("remessa_id").references(() => remontaDocumentRemittances.id), requisitoId: uuid("requisito_id"), anteriorId: uuid("anterior_id"), nome: text("nome").notNull(), tamanho: integer("tamanho").notNull(), tipo: text("tipo").notNull(), storagePath: text("storage_path").notNull().unique(), hashIntegridade: text("hash_integridade"), estado: text("estado").notNull().default("pendente"), confirmadoEm: timestamp("confirmado_em",{withTimezone:true}), criadoEm: timestamp("criado_em",{withTimezone:true}).notNull().defaultNow(),
}, table => [
  unique("remonta_file_portal_identity").on(table.id, table.acessoId, table.coletaId),
  foreignKey({ columns: [table.acessoId, table.coletaId], foreignColumns: [remontaDocumentoAcessos.id, remontaDocumentoAcessos.coletaId] }).onDelete("cascade"),
  foreignKey({ name: "remonta_file_remittance_scope", columns: [table.remessaId, table.acessoId, table.coletaId], foreignColumns: [remontaDocumentRemittances.id, remontaDocumentRemittances.acessoId, remontaDocumentRemittances.coletaId] }),
  foreignKey({ name: "remonta_file_previous_scope", columns: [table.anteriorId, table.acessoId, table.coletaId], foreignColumns: [table.id, table.acessoId, table.coletaId] }),
  index("remonta_arquivos_coleta_idx").on(table.coletaId, table.acessoId), index("remonta_arquivos_access_idx").on(table.acessoId),
  index("remonta_arquivos_remittance_idx").on(table.remessaId), index("remonta_arquivos_previous_idx").on(table.anteriorId),
  check("remonta_file_confirmation", sql`(${table.estado}='confirmado')=(${table.confirmadoEm} IS NOT NULL)`),
  check("remonta_file_submitted", sql`${table.remessaId} IS NULL OR ${table.estado}='confirmado'`),
]);
export const remontaDocumentRemittances = pgTable("remonta_document_remittances", {
  id: uuid("id").primaryKey().defaultRandom(),
  ordemServicoId: uuid("ordem_servico_id").references(() => remontaOrdensServico.id, { onDelete: "cascade" }),
  protocolo: uuid("protocolo").notNull().defaultRandom().unique(),
  remetenteNome: text("remetente_nome").notNull(), remetenteCargo: text("remetente_cargo").notNull(), remetenteEmail: text("remetente_email").notNull(),
  autorId: uuid("autor_id").references(() => userProfiles.id),
  coletaId: uuid("coleta_id").references(() => remontaDocumentoColetas.id), acessoId: uuid("acesso_id").references(() => remontaDocumentoAcessos.id), anteriorId: uuid("anterior_id"),
  autorizadoEnvio: boolean("autorizado_envio").notNull(), consentimentoVersao: text("consentimento_versao").notNull(), consentimentoTexto: text("consentimento_texto").notNull(),
  canalEnvio: text("canal_envio").notNull(), observacaoGeral: text("observacao_geral").notNull().default(""),
  referencias: text("referencias").notNull(), respostas: jsonb("respostas").notNull().$type<Array<Record<string, unknown>>>(),
  criadoEm: timestamp("criado_em", { withTimezone: true }).notNull().defaultNow(),
}, table => [
  unique("remonta_remittance_portal_identity").on(table.id, table.acessoId, table.coletaId),
  foreignKey({ name: "remonta_remittance_access_scope", columns: [table.acessoId, table.coletaId], foreignColumns: [remontaDocumentoAcessos.id, remontaDocumentoAcessos.coletaId] }).onDelete("cascade"),
  foreignKey({ name: "remonta_remittance_collection_order", columns: [table.coletaId, table.ordemServicoId], foreignColumns: [remontaDocumentoColetas.id, remontaDocumentoColetas.ordemServicoId] }).onDelete("cascade"),
  foreignKey({ name: "remonta_remittance_previous_scope", columns: [table.anteriorId, table.acessoId, table.coletaId], foreignColumns: [table.id, table.acessoId, table.coletaId] }),
  index("remonta_portal_remessas_idx").on(table.coletaId, table.acessoId, table.criadoEm),
  index("remonta_remittances_access_idx").on(table.acessoId), index("remonta_remittances_previous_idx").on(table.anteriorId),
  check("remonta_remittance_real_author", sql`(${table.autorId} IS NOT NULL AND ${table.acessoId} IS NULL AND ${table.coletaId} IS NULL AND ${table.ordemServicoId} IS NOT NULL) OR (${table.autorId} IS NULL AND ${table.acessoId} IS NOT NULL AND ${table.coletaId} IS NOT NULL)`),
]);

export const auditLogs = pgTable("audit_logs", {
  id: uuid("id").primaryKey().defaultRandom(),
  userId: uuid("user_id"), userNome: text("user_nome").notNull(),
  empresaId: uuid("empresa_id").notNull(), empresaNome: text("empresa_nome").notNull(),
  entidade: text("entidade").notNull(), entidadeId: text("entidade_id"), entidadeDescricao: text("entidade_descricao"),
  acao: text("acao").notNull(), detalhes: jsonb("detalhes").$type<Record<string, unknown>>(),
  documentoAcessoId: uuid("documento_acesso_id"), documentoColetaId: uuid("documento_coleta_id"),
  criadoEm: timestamp("criado_em", { withTimezone: true }).notNull().defaultNow(),
}, table => [
  foreignKey({ name: "remonta_audit_documental_author", columns: [table.documentoAcessoId, table.documentoColetaId], foreignColumns: [remontaDocumentoAcessos.id, remontaDocumentoAcessos.coletaId] }),
  check("remonta_audit_real_author", sql`(${table.userId} IS NOT NULL AND ${table.documentoAcessoId} IS NULL AND ${table.documentoColetaId} IS NULL) OR (${table.userId} IS NULL AND ${table.documentoAcessoId} IS NOT NULL AND ${table.documentoColetaId} IS NOT NULL)`),
  index("remonta_audit_documental_access_idx").on(table.documentoAcessoId, table.documentoColetaId),
]);
